import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Generate enhanced Excel template with instructions, data validation, and styling
 * 
 * Requirement 5: Enhanced Data Import Template with all required fields,
 * example data, field descriptions, and data validation.
 */
export async function generateEnhancedTemplate() {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'TUM Onboarding';
  workbook.created = new Date();

  // ===== INSTRUCTIONS SHEET =====
  const instructionsSheet = workbook.addWorksheet('Instructions');
  
  instructionsSheet.getColumn(1).width = 80;
  
  const instructions = [
    '📋 Data Import Template - Instructions',
    '',
    'Welcome to the TUM Onboarding Data Import Template!',
    '',
    '📝 How to Use This Template:',
    '1. Fill in the "Data" sheet with your account information',
    '2. Required fields are marked with * in column headers (red background)',
    '3. Use dropdown lists where provided (don\'t type manually)',
    '4. See example rows for format guidance',
    '5. Delete example rows before uploading',
    '6. Save as CSV or keep as XLSX and upload',
    '',
    '📊 Field Definitions:',
    '• Account Number* - Utility account number (unique identifier)',
    '• Property Name* - Name of the property',
    '• Property Code - Internal property code (optional)',
    '• Unit Number* - Unit/apartment number',
    '• Commodity* - Type of utility (Electricity, Water, Gas, etc.)',
    '• Vendor Name* - Utility company name',
    '• Meter Number - Physical meter identifier (optional)',
    '• GL Code - General Ledger code (optional)',
    '• Service Address - Full service address (optional)',
    '• Billing Address - Billing address if different (optional)',
    '• Notes - Any additional notes (optional)',
    '',
    '⚠️ Important:',
    '• All fields marked with * are REQUIRED',
    '• Account numbers must be unique',
    '• Use only values from dropdowns for Commodity and Vendor',
    '• Maximum 10,000 rows per import',
    '',
    '📧 Need Help? Contact: support@billportal.ai',
  ];

  instructions.forEach((text, index) => {
    const cell = instructionsSheet.getCell(index + 1, 1);
    cell.value = text;
    if (index === 0) {
      cell.font = { size: 18, bold: true };
    } else if (text.startsWith('📝') || text.startsWith('📊') || text.startsWith('⚠️') || text.startsWith('📧')) {
      cell.font = { size: 12, bold: true };
    }
  });

  // ===== DATA SHEET =====
  const dataSheet = workbook.addWorksheet('Data');

  const columns = [
    { header: 'Account #*', key: 'accountNumber', width: 15, required: true },
    { header: 'Property Name*', key: 'propertyName', width: 20, required: true },
    { header: 'Property Code', key: 'propertyCode', width: 15, required: false },
    { header: 'Unit #*', key: 'unitNumber', width: 10, required: true },
    { header: 'Commodity*', key: 'commodity', width: 15, required: true },
    { header: 'Vendor Name*', key: 'vendorName', width: 20, required: true },
    { header: 'Meter #', key: 'meterNumber', width: 15, required: false },
    { header: 'GL Code', key: 'glCode', width: 15, required: false },
    { header: 'Service Address', key: 'serviceAddress', width: 30, required: false },
    { header: 'Billing Address', key: 'billingAddress', width: 30, required: false },
    { header: 'Notes', key: 'notes', width: 20, required: false },
  ];

  dataSheet.columns = columns.map(c => ({ header: c.header, key: c.key, width: c.width }));

  // Style headers
  const headerRow = dataSheet.getRow(1);
  headerRow.eachCell((cell, colNumber) => {
    const col = columns[colNumber - 1];
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: col.required ? 'FFE53935' : 'FF1976D2' },
    };
    cell.alignment = { wrapText: true, vertical: 'middle' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // Freeze header row
  dataSheet.views = [{ state: 'frozen', ySplit: 1 }];

  // Add data validations (cast to any as ExcelJS types don't expose dataValidations.add)
  (dataSheet as any).dataValidations.add('E2:E10000', {
    type: 'list',
    allowBlank: false,
    formulae: ['"Electricity,Water,Gas,Sewer,Trash,Storm Drain,Irrigation"'],
    showErrorMessage: true,
    errorTitle: 'Invalid Commodity',
    error: 'Please select a commodity from the dropdown',
  });

  // Add example rows
  const exampleData = [
    {
      accountNumber: '123456789',
      propertyName: 'Sunset Apartments',
      propertyCode: 'PROP-001',
      unitNumber: '101',
      commodity: 'Electricity',
      vendorName: 'PG&E',
      meterNumber: 'E-9876543',
      glCode: '4100-001',
      serviceAddress: '123 Main St, Unit 101, San Francisco, CA 94102',
      billingAddress: 'Same',
      notes: '',
    },
    {
      accountNumber: '987654321',
      propertyName: 'Sunset Apartments',
      propertyCode: 'PROP-001',
      unitNumber: '102',
      commodity: 'Water',
      vendorName: 'Bay Area Water',
      meterNumber: 'W-1234567',
      glCode: '4200-001',
      serviceAddress: '123 Main St, Unit 102, San Francisco, CA 94102',
      billingAddress: 'Same',
      notes: '',
    },
    {
      accountNumber: '555666777',
      propertyName: 'Oak Towers',
      propertyCode: 'PROP-002',
      unitNumber: '201',
      commodity: 'Gas',
      vendorName: 'SoCalGas',
      meterNumber: 'G-8765432',
      glCode: '4300-002',
      serviceAddress: '456 Oak Ave, Suite 201, Los Angeles, CA 90001',
      billingAddress: 'Same',
      notes: 'Master meter',
    },
  ];

  exampleData.forEach(row => {
    dataSheet.addRow(row);
  });

  // Style example rows
  [2, 3, 4].forEach(rowNum => {
    const row = dataSheet.getRow(rowNum);
    row.eachCell(cell => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFF9C4' },
      };
      cell.font = { italic: true };
    });
  });

  // Add instruction row
  const instructionRow = dataSheet.addRow({
    accountNumber: '← Delete example rows above and start entering your data here',
  });
  instructionRow.getCell(1).font = { italic: true, color: { argb: 'FF666666' } };

  // Generate and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, 'TUM_Data_Import_Template.xlsx');
}

export default generateEnhancedTemplate;
